import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { Upload } from '../../../../assets';
import { useDropzone } from 'react-dropzone';
import { ConfirmationModal, Loader } from '../../../shared';
import { useDispatch, useSelector } from 'react-redux';
import { editCreativeUpload, getCreatives, postCreativeUpload } from '../../../../redux/thunks/creativeUploadThunk';
import { creativeUploadActions } from '../../../../redux/reducers/creativeUploadReducer';

const UploadCreative = () => {
    const dispatch = useDispatch();
    const {
        isUploadOpen, isUploading, modalType, creative 
    } = useSelector(
        (state) => state.creativeUpload
    );
    const { company } = useSelector((state) => state.company);

    const [fileData, setFileData] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [formData, setFormData] = useState({
        creativeName: '',
        creativeSize: '',
        startDate: '',
        endDate: ''
    });

    const maxSize = 50 * 1024 * 1024; // 50 MB

    useEffect(() => {
        if (modalType === 'Edit Creative' && creative) {
            setFormData({
                creativeName: creative.creative_name || '',
                creativeSize: creative.creative_size || '',
                startDate: creative.start_date || '',
                endDate: creative.end_date || ''
            });
        } else {
            resetForm();
        }
    }, [modalType, creative]);

    const resetForm = () => {
        setFileData(null);
        setFormData({
            creativeName: '',
            creativeSize: '',
            startDate: '',
            endDate: ''
        });
        setFileError(null);
    };

    const onDrop = useCallback((acceptedFiles, fileRejections) => {
        if (fileRejections.length > 0) {
            const errorMessages = fileRejections
                .map((rejection) => {
                    const { file, errors } = rejection;
                    return errors
                        .map((error) => {
                            if (error.code === 'file-too-large') {
                                return `File ${file.name} is too large. Maximum size is ${
                                    maxSize / (1024 * 1024)
                                } MB.`;
                            } else if (error.code === 'file-invalid-type') {
                                return `Invalid file type for ${file.name}. Please upload a supported image (jpg, jpeg, png).`;
                            }
                            return `Error with ${file.name}: ${error.message}`;
                        })
                        .join(' ');
                })
                .join(' ');

            setFileError(errorMessages);
            setFileData(null);
        } else {
            const file = acceptedFiles[0];
            setFileData(file);
            setFileError(null);
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg']
        },
        maxSize
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleUploadFile = () => {
        const {
            creativeName, creativeSize, startDate, endDate 
        } = formData;

        if (!fileData && modalType === 'Create Asset') {
            setFileError('Please upload a file.');
            return;
        }

        if (!creativeName || !creativeSize || !startDate || !endDate) {
            setFileError('Please fill in all fields.');
            return;
        }

        const form = new FormData();
        if (fileData) {
            form.append('file', fileData);
        }
        form.append('company_id', company.id);
        form.append('creative_name', creativeName);
        form.append('creative_size', creativeSize);
        form.append('start_date', startDate);
        form.append('end_date', endDate);
        if(modalType === 'Edit Creative'){
            dispatch(editCreativeUpload({ data: form,id: creative.id })).then((result) => {
                if (result.payload.status_code === 200) {
                    dispatch(getCreatives({ companyId: company.id }));
                    onClosed();
                } else {
                    setFileError('Failed to update. Please try again.');
                }
            });
        } else{
            dispatch(postCreativeUpload({ data: form })).then((result) => {
                if (result.payload.status_code === 200) {
                    dispatch(getCreatives({ companyId: company.id }));
                    onClosed();
                } else {
                    setFileError('Failed to upload. Please try again.');
                }
            });
        }
    };

    const onClosed = () => {
        resetForm();
        dispatch(creativeUploadActions.handleUploadModal({ type: 'create' }));
    };
    useEffect(() => {
        if (fileData) {
            const objectURL = URL.createObjectURL(fileData);
    
            return () => {
                URL.revokeObjectURL(objectURL);
            };
        }
    }, [fileData]);
    
    const renderUpload = () => (
        <Box className="flex-column-space-between gap-20 pt-24px">
            <Box className="pb-12px text-align-center">
                {(fileData || creative?.file_path) && (
                    <Box>
                        <Typography className="text-sm-medium gray-500">Preview:</Typography>
                        <img
                            alt="Preview"
                            src={
                                modalType === 'Edit Creative' 
                                    ? creative.file_path 
                                    : fileData 
                                        ? URL.createObjectURL(fileData) 
                                        : ''
                            }
                            style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '8px'
                            }}
                        />
                    </Box>
                )}
            </Box>
            <Box
                className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100 icon"
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                    <Upload />
                </Box>
                <Box className="flex-row gap-4">
                    <Typography className="text-sm-medium primary-600">
            Click to upload
                    </Typography>
                    <Typography className="text-sm-medium gray-500">
            or drag and drop
                    </Typography>
                </Box>
                <Box>
                    <Typography className="text-xs-regular gray-500">
            Files (max 50mb)
                    </Typography>
                </Box>
                <span className="text-lg-medium primary-600">{fileData?.name}</span>
                <span className="text-xs-regular error-500">{fileError}</span>
            </Box>
            <Box>
                <Grid container spacing={2}>
                    {['creativeName', 'creativeSize', 'startDate', 'endDate'].map((field) => (
                        <Grid item key={field} lg={6} md={6} xs={12}>
                            <Box className="flex-column gap-6 w-100 pb-24px">
                                <Typography className="text-sm-medium gray-500">
                                    {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                </Typography>
                                <input
                                    className="custom-text-field padding-tb-10-rl-14"
                                    name={field}
                                    placeholder={field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    type={field.includes('Date') ? 'date':'text'}
                                    value={formData[field]}
                                    onChange={handleInputChange}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            {isUploading && <Loader classNames="flex-row-center ml-40-percent" height="15%" width="15%" />}
        </Box>
    );

    return (
        <ConfirmationModal
            content={renderUpload()}
            customClass={true}
            open={isUploadOpen}
            primaryButtonText={modalType === 'Edit Creative' ? 'Save Changes' : 'Create'}
            title={<Typography className="queries-table-title">{modalType}</Typography>}
            onClose={onClosed}
            onConfirm={handleUploadFile}
        />
    );
};

export default UploadCreative;
